import React, { memo, useEffect } from 'react';
import { useReactiveVar } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Alert, Collapse, Spin } from 'antd';
import dayjs from 'dayjs';

import styled from 'styled-components';
import fontService, { FontWeight, TextType } from 'services/FontService';
import { desktop, mobile, tablet } from 'services/ui';
import { Colors, NetworkErrors, UserRole } from 'context';
import { Button, ButtonSizes } from 'atoms';
import { appState, authState, useMySubmittedSurveys, useMySurveys } from 'core';
import { PATHS } from 'router/config';

/**
 * /surveys — the student's surveys.
 *
 * Pending ones (spec 0011) as cards; below them, «Пройденные» (spec 0027) as a
 * collapsed archive with the student's own answers. The archive is separate from
 * `mySurveys` on purpose: the pending list feeds the sidebar badge and is
 * refetched constantly, and answer bodies must not ride along on every one of
 * those requests.
 *
 * Students only — mirrors the Invoices page role guard (teacher/methodist/demo →
 * 404); the archive query is `skip`ped for anyone else so a non-student never
 * even asks for it.
 */
export const Surveys = memo(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { active_role, isDemoUser } = useReactiveVar(authState);
  const currentState = appState();

  const isStudent = active_role === UserRole.STUDENT && !isDemoUser;
  const { surveys, loading, error } = useMySurveys();
  // No loading state for the archive: it renders below the pending list, which
  // already owns the page's spinner, and a second spinner appearing under it
  // reads as a broken page rather than as progress.
  const { surveys: submitted, error: submittedError } = useMySubmittedSurveys(!isStudent);

  useEffect(() => {
    if (active_role !== UserRole.STUDENT || isDemoUser) {
      appState({
        ...currentState,
        networkError: NetworkErrors.pageIsNotAvailable,
      });
    }
  }, [active_role, currentState, isDemoUser]);

  return (
    <SurveysPageWrapper>
      <SurveysHeader>
        <PageTitle>{t('surveys.title')}</PageTitle>
        <PageInfo>{t('surveys.info')}</PageInfo>
      </SurveysHeader>

      {error ? (
        <Alert type="error" showIcon message={t('surveys.loadError')} />
      ) : loading && !surveys.length ? (
        <SpinWrapper>
          <Spin size="large" />
        </SpinWrapper>
      ) : surveys.length ? (
        <CardsList>
          {surveys.map((survey) => (
            <SurveyCard key={survey.assignmentId}>
              <CardTitle>{survey.title}</CardTitle>
              {survey.description ? <CardDescription>{survey.description}</CardDescription> : null}
              <CardMeta>
                {survey.groupName} · {t('surveys.card.afterLesson', { n: survey.lessonNumber })}
              </CardMeta>
              <TakeButton
                size={ButtonSizes.sm}
                onClick={() => navigate(`${PATHS.surveys}/${survey.assignmentId}`)}>
                {t('surveys.card.take')}
              </TakeButton>
            </SurveyCard>
          ))}
        </CardsList>
      ) : (
        <EmptyState>{t('surveys.empty')}</EmptyState>
      )}

      {/* Spec 0027 — «Пройденные». Shown when there is something to show OR
          when the query failed: an empty archive on a brand-new account would be
          noise, but nesting the error inside the length check made a failed
          query render NOTHING — indistinguishable from «ты ничего не проходил»,
          because an error leaves the list empty too. */}
      {submitted.length || submittedError ? (
        <SubmittedSection>
          <SectionTitle>{t('surveys.submitted.title')}</SectionTitle>
          <SectionInfo>{t('surveys.submitted.info')}</SectionInfo>
          {submittedError ? (
            <Alert type="error" showIcon message={t('surveys.loadError')} />
          ) : (
            <Collapse ghost>
              {submitted.map((survey) => (
                <Collapse.Panel
                  key={survey.responseId}
                  header={
                    <PanelHeader>
                      <PanelTitle>{survey.title}</PanelTitle>
                      <PanelMeta>
                        {survey.groupName} ·{' '}
                        {t('surveys.card.afterLesson', { n: survey.lessonNumber })} ·{' '}
                        {t('surveys.submitted.on', {
                          date: dayjs(survey.submittedAt).format('DD.MM.YYYY'),
                        })}
                      </PanelMeta>
                    </PanelHeader>
                  }>
                  {survey.answers.map(({ question, display }) => (
                    <AnswerRow key={question.id}>
                      <AnswerQuestion>
                        {question.number}. {question.text}
                      </AnswerQuestion>
                      <AnswerValue>
                        {display === ''
                          ? t('surveys.submitted.noAnswer')
                          : /* yes_no travels as a token — the transform cannot
                               localise, so the label is resolved here. */
                          question.questionType === 'yes_no'
                          ? t(`surveys.submitted.${display}`)
                          : display}
                      </AnswerValue>
                    </AnswerRow>
                  ))}
                </Collapse.Panel>
              ))}
            </Collapse>
          )}
        </SubmittedSection>
      ) : null}
    </SurveysPageWrapper>
  );
});

const SurveysPageWrapper = styled.div`
  width: 100%;
  margin-bottom: 80px;

  ${desktop} {
    margin-bottom: 104px;
  }
`;

const SurveysHeader = styled.div`
  margin: 32px 0;

  ${tablet} {
    margin: 40px 0 32px 0;
  }

  ${desktop} {
    margin: 54px 0 32px 0;
  }
`;

const PageTitle = styled.h2`
  margin: 0 0 4px 0;
  ${fontService.text({ size: 18, lineHeight: 133, weight: FontWeight.bold })}

  ${tablet} {
    margin: 0 0 8px 0;
    ${fontService.h3()}
  }

  ${desktop} {
    ${fontService.h2()}
  }
`;

const PageInfo = styled.p`
  margin: 0;
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}

  ${tablet} {
    ${fontService.text({ type: TextType.normal, weight: FontWeight.regular })}
  }

  ${desktop} {
    ${fontService.text({ size: 18, lineHeight: 133, weight: FontWeight.regular })}
  }
`;

const SpinWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 48px 0;
`;

const CardsList = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SurveyCard = styled.div`
  width: 100%;
  box-sizing: border-box;
  padding: 20px 24px;
  border: 1px solid ${Colors.additional_gray};
  border-radius: 12px;
  background-color: ${Colors.white};
`;

const CardTitle = styled.div`
  ${fontService.text({ size: 16, lineHeight: 133, weight: FontWeight.bold })}
  margin-bottom: 4px;
`;

const CardDescription = styled.div`
  ${fontService.text({ type: TextType.normal, weight: FontWeight.regular })}
  margin-bottom: 8px;
  white-space: pre-wrap;
`;

const CardMeta = styled.div`
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}
  color: ${Colors.gray_dark};
`;

const TakeButton = styled(Button)`
  width: auto;
  margin-top: 16px;

  ${mobile} {
    width: 100%;
  }
`;

const SubmittedSection = styled.div`
  width: 100%;
  margin-top: 40px;
`;

const SectionTitle = styled.h3`
  margin: 0 0 4px 0;
  ${fontService.text({ size: 16, lineHeight: 133, weight: FontWeight.bold })}

  ${tablet} {
    ${fontService.h3()}
  }
`;

const SectionInfo = styled.p`
  margin: 0 0 12px 0;
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}
  color: ${Colors.gray_dark};
`;

const PanelHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const PanelTitle = styled.div`
  ${fontService.text({ type: TextType.normal, weight: FontWeight.bold })}
`;

const PanelMeta = styled.div`
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}
  color: ${Colors.gray_dark};
`;

const AnswerRow = styled.div`
  padding: 8px 0;
  border-bottom: 1px solid ${Colors.additional_gray};

  &:last-child {
    border-bottom: none;
  }
`;

const AnswerQuestion = styled.div`
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}
  color: ${Colors.gray_dark};
`;

const AnswerValue = styled.div`
  margin-top: 2px;
  ${fontService.text({ type: TextType.normal, weight: FontWeight.regular })}
  white-space: pre-wrap;
`;

const EmptyState = styled.div`
  width: 100%;
  padding: 48px 0;
  text-align: center;
  ${fontService.text({ size: 16, lineHeight: 133, weight: FontWeight.regular })}
  color: ${Colors.gray_dark};
`;
